import { FC, Fragment, useEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import { Trans, useTranslation } from 'next-i18next';
import Button from 'src/components/Button';
import Gap from 'src/components/Gap';
import ResizingText from 'src/components/ResizingText';
import Benefit, { BenefitProps } from 'src/components/Vip/Benefit';
import Progress from 'src/components/Vip/Progress';
import Terms from 'src/components/Vip/Terms';
import { useGetGemProductsQuery } from 'src/hooks/useGemProductQuery';
import useLogin from 'src/hooks/useLogin';
import useSelectedTab from 'src/hooks/useSelectedTab';
import useShowPayment from 'src/hooks/useShowPayment';
import { userDataAtom, userInventoryAtom } from 'src/stores/auth/atoms';
import { purchasableSubscriptionProductsAtom } from 'src/stores/payments/atoms';
import { SHOP_ORIGIN } from 'src/stores/shop/types';
import { currentRatingIndexAtom, getVipRatingDetailsAtom, selectedRatingIndexAtom, vipRatingDetailsAtom } from 'src/stores/vip/atoms';
import { EVENT_NAME, EVENT_TYPE } from 'src/types/Event';
import { VipBenefit } from 'src/types/VIP';
import { numComma } from 'src/utils/common';
import { pushEvent } from 'src/utils/event-gateway';
import { startPurchase } from 'src/utils/payments';
const Wrap = styled.div`
  display: flex;
  padding: 16px;
  width: 100%;
  flex-direction: column;
  background-color: ${({
  theme
}) => theme.color.bg1};
`;
const Container = styled.div`
  z-index: 1;
  display: flex;
  padding: 32px 24px;
  flex-direction: column;
  background-color: ${({
  theme
}) => theme.color.surface3};
  border-radius: 24px;
  margin-bottom: 8px;
`;
const BenefitSection = styled.div`
  width: 100%;
`;
const BenefitIconBase = css`
  width: 40px;
  height: 40px;
`;
const BenefitIcon = styled.img`
  ${BenefitIconBase}
`;
const BenefitIconSource = styled.source`
  ${BenefitIconBase}
`;
const BenefitTabList = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  height: 80px;
  width: 100%;
  border: 1px solid ${({
  theme
}) => theme.color.surface5};
  border-radius: 16px;
  margin-bottom: 24px;
`;
const BenefitTab = styled.button`
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  background-color: transparent;
  align-items: center;
  justify-content: center;
  padding-top: 13px;
  padding-bottom: 13px;
  cursor: pointer;
  height: 80px;

  font-weight: 700;
  font-size: 12px;
  line-height: 14px;
  text-align: center;
  color: ${({
  theme
}) => theme.color.content11};
`;
const BenefitIndicator = styled.div<{
  offset: number;
  count: number;
}>`
  border: 2px solid ${({
  theme
}) => theme.color.surface11};
  border-radius: 16px;
  height: calc(100% + 2px);
  position: absolute;
  top: -1px;
  ${({
  count,
  offset,
  theme
}) => `
    width: calc((100% - (${count} * 2px)) / ${count});
    ${theme.isRtl ? 'right' : 'left'}: calc(100% / ${count} * ${offset} + ${offset}px);
    transition: ${theme.isRtl ? 'right' : 'left'} .3s;
  `}
`;
const BenefitTabPanelWrap = styled.div`
  position: relative;
  transition: height 0.3s;
  overflow: hidden;
`;
const BenefitTabPanel = styled.div<{
  active: boolean;
}>`
  position: absolute;
  top: 0;
  width: 100%;
  transition: opacity 0.3s;
  opacity: ${({
  active
}) => active ? 1 : 0};
  z-index: ${({
  active
}) => active ? 2 : 1};
  > * + * {
    margin-top: 16px;
  }
`;
const Title = styled.div`
  white-space: pre-wrap;
  font-weight: 700;
  font-size: 22px;
  line-height: 26px;
  color: ${({
  theme
}) => theme.color.content12};
`;
const BenefitSectionTitle = styled.div`
  font-weight: bold;
  font-size: 16px;
  line-height: 19px;
  color: ${({
  theme
}) => theme.color.content12};
  margin-bottom: 19px;
`;
const CurrentRating = styled.span<{
  color?: string;
}>`
  color: ${({
  color,
  theme
}) => color || theme.color.content8};
`;
const DescRow = styled.div`
  display: flex;
  flex-direction: row;
`;
const DescList = styled.div`
  display: flex;
  flex-direction: column;
  & > ${DescRow} + ${DescRow} {
    margin-top: 8px;
  }
`;
const DescIcon = styled.img`
  width: 16px;
  height: 16px;
`;
const DescText = styled.div`
  font-size: 14px;
  line-height: 17px;
  word-break: keep-all;
  word-wrap: break-word;
  white-space: pre-wrap;
  color: ${({
  theme
}) => theme.color.content11};
`;
const CurrentDate = styled.div`
  font-weight: 700;
  font-size: 14px;
  line-height: 17px;
  color: ${({
  theme
}) => theme.color.content9};
  margin-bottom: 4px;
`;
const VipButtonWrap = styled.div`
  margin-top: 27px;
  display: flex;
  flex-direction: column;
  > * + * {
    margin-top: 12px;
  }
`;
const VipButton = styled(Button)`
  font-size: 16px;
  line-height: 19px;
`;
const VipRatingView: FC = () => {
  const {
    t
  } = useTranslation();
  const router = useRouter();
  const showPayment = useShowPayment();
  const currentIndex = useAtomValue(currentRatingIndexAtom);
  const {
    isLogin
  } = useLogin();
  const [selected, setSelected] = useAtom(selectedRatingIndexAtom);
  const tabPanelsRef = useRef<HTMLDivElement[]>([]);
  const [tabPanelHeights, setTabPanelHeights] = useState<number[]>([]);
  const {
    data: gemProductsData
  } = useGetGemProductsQuery();
  const gemProducts = gemProductsData?.data?.gemProducts;
  const supremeSubscription = useAtomValue(userInventoryAtom)?.find(({
    categoryId
  }) => categoryId === 'AZAR_SUPREME');
  const getVipRatingDetails = useSetAtom(getVipRatingDetailsAtom);
  const vipRatingDetails = useAtomValue(vipRatingDetailsAtom);
  const azarUser = useAtomValue(userDataAtom);
  const isSupremePurchasable = useAtomValue(purchasableSubscriptionProductsAtom)?.find(({
    groupId
  }) => groupId === 'INTEGRATED_SUBSCRIPTION')?.products.some(({
    type
  }) => type === 'AZAR_SUPREME');
  const {
    selectedTab
  } = useSelectedTab();
  useEffect(() => {
    if (isLogin) {
      getVipRatingDetails();
    }
  }, [getVipRatingDetails, isLogin]);
  useEffect(() => {
    if (router.isReady) {
      pushEvent({
        eventType: EVENT_TYPE.VIP,
        eventName: EVENT_NAME.VIP_SYSTEM__IMPRESSION,
        eventParams: {
          land_from: router.query.land_from
        }
      });
    }
  }, [router.query, router.isReady]);
  useEffect(() => {
    if (vipRatingDetails) {
      const handler = () => {
        setTabPanelHeights(tabPanelsRef.current.map(el => el.offsetHeight));
      };
      window.addEventListener('resize', handler);
      handler();
      return () => {
        window.removeEventListener('resize', handler);
      };
    }
  }, [vipRatingDetails]);
  if (!vipRatingDetails || !azarUser) {
    return null;
  }
  const {
    ratings,
    benefits,
    userRating,
    refundPolicyText
  } = vipRatingDetails;
  const renderBenefits = (vipBenefits: VipBenefit[], benefitProps: Omit<BenefitProps, keyof VipBenefit>) => vipBenefits.map(vipBenefit => <Benefit key={`benefit-row-${vipBenefit.title}`} {...benefitProps} {...vipBenefit} />);
  const onClickSubscribeButton = () => {
    const platform = window.AzarJs?.getPlatform();
    if (!platform) {
      return;
    }
    if (supremeSubscription) {
      if (!userRating.recommendSubscriptionProductId) {
        return;
      }
      if (platform === 'ANDROID') {
        window.location.href = `intent://play.google.com/store/account/subscriptions?product=${userRating.recommendSubscriptionProductId}&package=com.azarlive.android/#Intent;scheme=https;package=com.android.vending;end`;
        return;
      } else if (platform === 'IOS') {
        window.location.href = 'azarlive://subs_popup?pageId=AZAR_SUPREME&initial_feature=vip_system&land_from=vip_system';
        return;
      }
    }
    if (platform === 'ANDROID') {
      window.location.href = 'intent://subs_popup?pageId=AZAR_SUPREME&initial_feature=vip_system&land_from=vip_system/#Intent;scheme=azarlive;package=com.azarlive.android;end';
    } else {
      window.location.href = 'azarlive://subs_popup?pageId=AZAR_SUPREME&initial_feature=vip_system&land_from=vip_system';
    }
  };
  const onClickVipButton = () => {
    if (!userRating.recommendGemProductId) {
      return;
    }
    pushEvent({
      eventType: EVENT_TYPE.VIP,
      eventName: EVENT_NAME.VIP_SYSTEM__CLICK_PURCHASE,
      eventParams: {
        product_id: userRating.recommendGemProductId,
        grade: userRating.nextRatingGoal
      }
    });
    if (window.AzarJs) {
      window.purchaseFinished = (productId, success) => {
        if (success) {
          pushEvent({
            eventType: EVENT_TYPE.VIP,
            eventName: EVENT_NAME.VIP_SYSTEM__PURCHASE_DONE,
            eventParams: {
              product_id: userRating.recommendGemProductId,
              grade: userRating.nextRatingGoal
            }
          });
          getVipRatingDetails();
        }
      };
      startPurchase(userRating.recommendGemProductId);
    } else {
      const gemProduct = gemProducts?.find(({
        productId
      }) => productId === userRating.recommendGemProductId);
      if (!gemProduct) {
        return;
      }
      showPayment({
        product: gemProduct,
        origins: [SHOP_ORIGIN.vipRating],
        originTab: selectedTab.name
      });
    }
  };
  const hasSupremeButton = isSupremePurchasable && !!userRating.subscribeButtonText && !!userRating.recommendSubscriptionProductId && !supremeSubscription?.autoRenewing;
  const hasGemButton = userRating.recommendGemProductId && userRating.purchaseButtonText;
  return <Wrap data-sentry-element="Wrap" data-sentry-component="VipRatingView" data-sentry-source-file="VipRating.tsx">
      <Container data-sentry-element="Container" data-sentry-source-file="VipRating.tsx">
        <CurrentDate data-sentry-element="CurrentDate" data-sentry-source-file="VipRating.tsx">
          {t('vipsystem_main_date', {
          year: userRating.year,
          month: userRating.month
        })}
        </CurrentDate>
        <Title data-sentry-element="Title" data-sentry-source-file="VipRating.tsx">
          <Trans i18nKey={userRating.currentRating === userRating.defaultRating ? 'vipsystem_main_basic_grade' : 'vipsystem_main_grade'} values={{
          nickName: azarUser.userProfile.simpleName,
          thisMonth: userRating.month,
          currentGrade: userRating.currentRating,
          nextGrade: userRating.nextRatingGoal || userRating.nextRating
        }} components={{
          CurrentRating: <CurrentRating color={ratings[currentIndex].ratingTextColor} />
        }} data-sentry-element="Trans" data-sentry-source-file="VipRating.tsx" />
        </Title>
      </Container>
      <Container data-sentry-element="Container" data-sentry-source-file="VipRating.tsx">
        <BenefitSectionTitle data-sentry-element="BenefitSectionTitle" data-sentry-source-file="VipRating.tsx">{t('vipsystem_main_next_title')}</BenefitSectionTitle>
        <DescList data-sentry-element="DescList" data-sentry-source-file="VipRating.tsx">
          <DescRow data-sentry-element="DescRow" data-sentry-source-file="VipRating.tsx">
            <DescIcon src='/images/icons/imgGemSquare.png' alt='Purhcased Gems' data-sentry-element="DescIcon" data-sentry-source-file="VipRating.tsx" />
            <Gap width={8} data-sentry-element="Gap" data-sentry-source-file="VipRating.tsx" />
            <DescText data-sentry-element="DescText" data-sentry-source-file="VipRating.tsx">
              {t('vipsystem_main_gem_now', {
              currentGemsAmount: numComma(userRating.purchaseGemAmount)
            })}
            </DescText>
          </DescRow>
          <DescRow data-sentry-element="DescRow" data-sentry-source-file="VipRating.tsx">
            <DescIcon src={userRating.nextRatingGoal ? '/images/vip/next.svg' : '/images/vip/check.svg'} alt='Remaining Gems for next rating' data-sentry-element="DescIcon" data-sentry-source-file="VipRating.tsx" />
            <Gap width={8} data-sentry-element="Gap" data-sentry-source-file="VipRating.tsx" />
            <DescText data-sentry-element="DescText" data-sentry-source-file="VipRating.tsx">
              <Trans i18nKey={userRating.nextRatingGoal ? 'vipsystem_main_gem_target' : 'vipsystem_main_gem_target2'} values={{
              targetGemsAmount: userRating.remainingGemAmount,
              nextGrade: userRating.nextRatingGoal || userRating.nextRating
            }} components={{
              strong: <strong />,
              CurrentRating: <CurrentRating color={ratings.find(({
                rating
              }) => rating === (userRating.nextRatingGoal || userRating.nextRating))?.ratingTextColor} />
            }} data-sentry-element="Trans" data-sentry-source-file="VipRating.tsx" />
            </DescText>
          </DescRow>
        </DescList>
        <Progress ratings={ratings} userRating={userRating} data-sentry-element="Progress" data-sentry-source-file="VipRating.tsx" />
        {(hasSupremeButton || hasGemButton) && <VipButtonWrap>
            {hasSupremeButton && <VipButton onClick={onClickSubscribeButton} id='vip-subscribe-supreme-button' variant='contained' color='secondary'>
                <ResizingText minFontSize={12}>{userRating.subscribeButtonText}</ResizingText>
              </VipButton>}
            {hasGemButton && <VipButton onClick={onClickVipButton} id='vip-recommend-gem-button' variant={hasSupremeButton ? 'outlined' : 'contained'} color='secondary'>
                <ResizingText minFontSize={12}>{userRating.purchaseButtonText}</ResizingText>
              </VipButton>}
          </VipButtonWrap>}
      </Container>

      <Container data-sentry-element="Container" data-sentry-source-file="VipRating.tsx">
        <BenefitSectionTitle data-sentry-element="BenefitSectionTitle" data-sentry-source-file="VipRating.tsx">{t('vipsystem_main_benefit_title')}</BenefitSectionTitle>
        <BenefitSection data-sentry-element="BenefitSection" data-sentry-source-file="VipRating.tsx">
          <BenefitTabList data-sentry-element="BenefitTabList" data-sentry-source-file="VipRating.tsx">
            {benefits.map(({
            rating,
            activeIconUrl,
            iconUrl
          }, i) => <Fragment key={`BenefitTab-${rating}`}>
                {i !== 0 && <Gap width={2} />}
                <BenefitTab type='button' onClick={() => setSelected(i)}>
                  <picture>
                    {selected === i && <BenefitIconSource srcSet={activeIconUrl} type='image/webp' />}
                    <BenefitIconSource srcSet={iconUrl} type='image/png' />
                    <BenefitIcon src={iconUrl} alt={`Click to check Benefits - ${rating}`} />
                  </picture>
                  {rating}
                </BenefitTab>
              </Fragment>)}
            <BenefitIndicator offset={selected} count={benefits.length} data-sentry-element="BenefitIndicator" data-sentry-source-file="VipRating.tsx" />
          </BenefitTabList>
          <BenefitTabPanelWrap style={{
          height: tabPanelHeights[selected]
        }} data-sentry-element="BenefitTabPanelWrap" data-sentry-source-file="VipRating.tsx">
            {benefits.map(({
            rating,
            basicBenefits,
            additionalBenefits
          }, i) => {
            const benefitProps = {
              rating,
              isUpperRating: i >= currentIndex
            };
            return <BenefitTabPanel key={`BenefitTabPanel-${rating}`} ref={el => {
              if (el) {
                tabPanelsRef.current[i] = el;
              }
            }} active={selected === i}>
                  {renderBenefits(basicBenefits, benefitProps)}
                  {!!additionalBenefits.length && <>{renderBenefits(additionalBenefits, benefitProps)}</>}
                </BenefitTabPanel>;
          })}
          </BenefitTabPanelWrap>
        </BenefitSection>
      </Container>
      <Terms refundPolicyText={refundPolicyText} data-sentry-element="Terms" data-sentry-source-file="VipRating.tsx" />
    </Wrap>;
};
export default VipRatingView;